import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { canSwitchSocietyContext, normalizeRole } from '../../shared/constants/roles';
import { societyApi } from '../../features/societies/api';
import { getErrorMessage } from '../../shared/api/client';
import { storage } from '../../shared/lib/storage';

export const fetchSocieties = createAsyncThunk('context/fetchSocieties', async (_, { rejectWithValue }) => {
  try {
    const response = await societyApi.getSocieties({ page: 1, limit: 100 });
    const data = response.data?.data || [];

    return data.map((society) => ({
      id: society.id,
      name: society.name,
      operationalState: society.operationalState || null,
    }));
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

const initialState = {
  activeSocietyId: storage.getActiveSocietyId(),
  allowedSocietyIds: [],
  societies: [],
  status: 'idle',
  error: null,
};

const contextSlice = createSlice({
  name: 'context',
  initialState,
  reducers: {
    setActiveSocietyId: (state, action) => {
      state.activeSocietyId = action.payload || null;
      storage.setActiveSocietyId(state.activeSocietyId);
    },
    updateSocietyOperationalState: (state, action) => {
      const societyId = action.payload?.societyId;
      const operationalState = action.payload?.operationalState;
      const society = state.societies.find((item) => item.id === societyId);
      if (society && operationalState) {
        society.operationalState = operationalState;
      }
    },
    initializeContext: (state, action) => {
      const { role, user, societyIdFromUrl } = action.payload;
      const normalizedRole = normalizeRole(role || user?.role);

      if (canSwitchSocietyContext(normalizedRole)) {
        const resolvedSocietyId = societyIdFromUrl || storage.getActiveSocietyId() || state.activeSocietyId;
        state.allowedSocietyIds = state.societies.map((society) => society.id);
        state.activeSocietyId =
          resolvedSocietyId && state.allowedSocietyIds.includes(resolvedSocietyId)
            ? resolvedSocietyId
            : state.societies[0]?.id || null;
        storage.setActiveSocietyId(state.activeSocietyId);
        return;
      }

      const fixedSocietyId = user?.society?.id || user?.societyId || null;
      state.activeSocietyId = fixedSocietyId;
      state.allowedSocietyIds = fixedSocietyId ? [fixedSocietyId] : [];
      storage.setActiveSocietyId(fixedSocietyId);
    },
    resetContext: (state) => {
      state.activeSocietyId = null;
      state.allowedSocietyIds = [];
      state.societies = [];
      state.status = 'idle';
      state.error = null;
      storage.setActiveSocietyId(null);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocieties.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSocieties.fulfilled, (state, action) => {
        state.status = 'ready';
        state.societies = action.payload;
        state.allowedSocietyIds = action.payload.map((society) => society.id);

        if (state.activeSocietyId && !state.allowedSocietyIds.includes(state.activeSocietyId)) {
          state.activeSocietyId = null;
          storage.setActiveSocietyId(null);
        }

        if (!state.activeSocietyId && action.payload.length > 0) {
          state.activeSocietyId = action.payload[0].id;
          storage.setActiveSocietyId(state.activeSocietyId);
        }
      })
      .addCase(fetchSocieties.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload || 'Unable to load societies';
        state.societies = [];
        state.allowedSocietyIds = [];
      });
  },
});

export const { setActiveSocietyId, updateSocietyOperationalState, initializeContext, resetContext } =
  contextSlice.actions;
export default contextSlice.reducer;
